import { useCallback, useMemo, useState } from 'react';

import { useOptionalMultiSelect } from '@/features/content/editor/ContentEditModeProvider';
import type {
  QuestionActionScope,
  QuestionSelectionState,
} from './questionTypes';

const emptySelectedQuestionIds = new Set<string>();

export function useQuestionSelection() {
  const multiSelect = useOptionalMultiSelect();
  const clearMultiSelectSelection = multiSelect?.clearSelection;
  const enterMultiSelectSelection = multiSelect?.enterSelection;
  const isMultiSelectSelectionActiveForScope =
    multiSelect?.isSelectionActiveForScope;
  const multiSelectScope = multiSelect?.selectMultipleScope;
  const multiSelectSelectedCount = multiSelect?.selectedCount ?? 0;
  const multiSelectSelectedRowIds = multiSelect?.selectedRowIds;
  const toggleMultiSelectRowSelection = multiSelect?.toggleRowSelection;
  const [state, setState] = useState<QuestionSelectionState>(() => ({
    activeScope: null,
    selectedQuestionIds: new Set<string>(),
  }));

  const selectedCount = state.selectedQuestionIds.size;

  const enterSelection = useCallback((
    scope: QuestionActionScope,
    initialQuestionId: string,
  ) => {
    setState({
      activeScope: scope,
      selectedQuestionIds: new Set([initialQuestionId]),
    });
  }, []);

  const cancelSelection = useCallback(() => {
    setState({
      activeScope: null,
      selectedQuestionIds: new Set<string>(),
    });
  }, []);

  const toggleQuestion = useCallback((
    scope: QuestionActionScope,
    questionId: string,
  ) => {
    setState((currentState) => {
      if (currentState.activeScope !== scope) {
        return {
          activeScope: scope,
          selectedQuestionIds: new Set([questionId]),
        };
      }

      const nextSelectedIds = new Set(currentState.selectedQuestionIds);

      if (nextSelectedIds.has(questionId)) {
        nextSelectedIds.delete(questionId);
      } else {
        nextSelectedIds.add(questionId);
      }

      return {
        activeScope: scope,
        selectedQuestionIds: nextSelectedIds,
      };
    });
  }, []);

  const selectedQuestionIds = useMemo(
    () => Array.from(state.selectedQuestionIds),
    [state.selectedQuestionIds],
  );

  const isSelectionActiveForScope = useCallback(
    (scope: QuestionActionScope) => state.activeScope === scope,
    [state.activeScope],
  );

  const contextSelectedQuestionIdsSet =
    (multiSelectScope === 'homework' || multiSelectScope === 'preLecture') &&
    multiSelectSelectedRowIds
      ? multiSelectSelectedRowIds
      : emptySelectedQuestionIds;
  const contextSelectedQuestionIds = useMemo(
    () => Array.from(contextSelectedQuestionIdsSet),
    [contextSelectedQuestionIdsSet],
  );
  const contextCancelSelection = useCallback(() => {
    clearMultiSelectSelection?.();
  }, [clearMultiSelectSelection]);
  const contextEnterSelection = useCallback(
    (scope: QuestionActionScope, initialQuestionId: string) => {
      enterMultiSelectSelection?.(scope, initialQuestionId);
    },
    [enterMultiSelectSelection],
  );
  const contextToggleQuestion = useCallback(
    (scope: QuestionActionScope, questionId: string) => {
      toggleMultiSelectRowSelection?.(scope, questionId);
    },
    [toggleMultiSelectRowSelection],
  );
  const contextIsSelectionActiveForScope = useCallback(
    (scope: QuestionActionScope) =>
      Boolean(isMultiSelectSelectionActiveForScope?.(scope)),
    [isMultiSelectSelectionActiveForScope],
  );

  if (multiSelect) {
    const activeScope =
      multiSelectScope === 'homework' ||
      multiSelectScope === 'preLecture'
        ? multiSelectScope
        : null;
    const isSelectionActive = activeScope !== null;

    return {
      activeScope,
      cancelSelection: contextCancelSelection,
      enterSelection: contextEnterSelection,
      isSelectionActive,
      isSelectionActiveForScope: contextIsSelectionActiveForScope,
      selectedCount: isSelectionActive
        ? multiSelectSelectedCount
        : 0,
      selectedQuestionIds: isSelectionActive
        ? contextSelectedQuestionIds
        : [],
      selectedQuestionIdsSet: contextSelectedQuestionIdsSet,
      state: {
        activeScope,
        selectedQuestionIds: contextSelectedQuestionIdsSet,
      },
      toggleQuestion: contextToggleQuestion,
    };
  }

  return {
    activeScope: state.activeScope,
    cancelSelection,
    enterSelection,
    isSelectionActive: state.activeScope !== null,
    isSelectionActiveForScope,
    selectedCount,
    selectedQuestionIds,
    selectedQuestionIdsSet: state.selectedQuestionIds,
    state,
    toggleQuestion,
  };
}
